import {
  getStudentByCode,
  orUndefined,
  type RedeemCodeResponse,
  redeemCode,
  searchGroupsPublic,
} from '@lms/api-client/backend';
import type { Option } from '@lms/ui/autocomplete';
import { z } from 'zod';

const submitCodeSchema = z.object({
  code: z.string().min(1).max(10),
  groupId: z.number().optional(),
  groupName: z.string().optional(),
});

export interface SubmitCodeInput {
  slug: string;
  code: string;
  groupId?: number;
  groupName?: string;
}

export type SubmitCodeResult =
  | { kind: 'redirect'; to: string }
  | {
      kind: 'select_group';
      groups: { id: string; name: string }[];
      groupType: 'tenant' | 'supplier';
    }
  | { kind: 'enter_supplier' }
  | { kind: 'error'; message: string };

/**
 * Trainee code redemption state machine. Ports `submitCode` from the Next app's
 * features/student/actions.ts: a server action that used `redirect()` becomes a
 * plain async function returning a discriminated union the caller acts on.
 */
export async function submitCode(
  input: SubmitCodeInput,
): Promise<SubmitCodeResult> {
  const parsed = submitCodeSchema.safeParse({
    code: input.code,
    groupId: input.groupId,
    groupName: input.groupName,
  });

  if (!parsed.success) {
    return { kind: 'error', message: 'unknown_code' };
  }

  const { code, groupId, groupName } = parsed.data;

  // First, try to look up the code as a personal code (student already exists).
  const existingStudent = await orUndefined(getStudentByCode(code), [400, 404]);
  if (existingStudent) {
    return {
      kind: 'redirect',
      to: `/${existingStudent.siteSlug ?? input.slug}/me/${code}`,
    };
  }

  // Try to redeem the code via the backend.
  let response: RedeemCodeResponse;
  try {
    response = await redeemCode({ code, groupId, groupName });
  } catch {
    return { kind: 'error', message: 'unknown_code' };
  }

  switch (response.type) {
    case 'success':
    case 'existing':
      return {
        kind: 'redirect',
        to: `/${response.siteSlug ?? input.slug}/me/${response.personalCode}`,
      };
    case 'select_group':
      return {
        kind: 'select_group',
        groups: response.groups.map((g) => ({ id: `${g.id}`, name: g.name })),
        groupType: response.groupType,
      };
    case 'enter_supplier':
      return { kind: 'enter_supplier' };
  }
}

/**
 * Search for groups by name within a location. Used for custom supplier code
 * entry where users can search existing groups.
 */
export async function searchGroups(
  locationId: number,
  query: string,
): Promise<Option[]> {
  if (!query) {
    return [];
  }
  const groups = await searchGroupsPublic(locationId, query);
  return groups.map((g) => ({ label: g.name, value: `${g.id}` }));
}
